import React, { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  uploadFileToAWS,
  deleteFileFromAWS
} from "../../../core/data/redux/nocExIndiaSlice";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
  formatFileSize,
} from "../../../utils/fileUtils";
import { getRequestStatusLabel } from "../../../core/data/constants/requestStatus";
import "./LeaveApproval.scss";

interface NOCApprovalDetailPageProps { }

const NOCApprovalDetailPage: React.FC<NOCApprovalDetailPageProps> = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const nocType = searchParams.get("type") || "NOC_OUTJOB";
  const routes = all_routes;
  const dispatch = useDispatch<any>();
  const [isMobile, setIsMobile] = useState(false);

  const formatStatusText = (status: string | undefined | null): string => {
    if (!status) return "Pending";
    const s = String(status).trim();
    const upper = s.toUpperCase();
    if (upper === "T" || upper === "TO BE APPROVED") return "To Be Approved";
    if (upper === "A" || upper === "APPROVED") return "Approved";
    if (upper === "R" || upper === "REJECTED") return "Rejected";
    if (upper === "P" || upper === "PENDING" || upper === "PARTIALLY APPROVED") return "Partially Approved";
    if (upper === "D" || upper === "PROCESSING COMPLETE") return "Processing Complete";
    if (upper === "N" || upper === "NEW") return "New";
    if (upper === "K" || upper === "CANCELLED") return "Cancelled";
    if (upper === "X" || upper === "DELETED") return "Deleted";
    if (upper === "B" || upper === "TO BE PROCESSED") return "To Be Processed";
    if (upper === "C" || upper === "CLOSED") return "Closed";
    if (upper === "S" || upper === "SETTLED") return "Settled";
    return getRequestStatusLabel(upper) || s;
  };

  const formatStatusBadgeClass = (status: string | undefined | null): string => {
    if (!status) return "badge bg-warning text-dark";
    const s = String(status).trim();
    const upper = s.toUpperCase();
    if (
      upper === "T" ||
      upper === "TO BE APPROVED" ||
      upper === "PENDING" ||
      upper === "P" ||
      upper === "PARTIALLY APPROVED" ||
      upper === "B" ||
      upper === "TO BE PROCESSED"
    ) {
      return "badge bg-warning text-dark";
    }
    if (
      upper === "A" ||
      upper === "APPROVED" ||
      upper === "D" ||
      upper === "PROCESSING COMPLETE" ||
      upper === "S" ||
      upper === "SETTLED"
    ) {
      return "badge bg-success";
    }
    if (
      upper === "R" ||
      upper === "REJECTED" ||
      upper === "K" ||
      upper === "CANCELLED" ||
      upper === "X" ||
      upper === "DELETED"
    ) {
      return "badge bg-danger";
    }
    if (upper === "N" || upper === "NEW") {
      return "badge bg-info text-dark";
    }
    return "badge bg-secondary";
  };

  // State for Higher Studies specific features
  const [remarks, setRemarks] = useState("");
  const [uploadedDocuments, setUploadedDocuments] = useState<
    Array<{
      file: File;
      key: string;
      url: string;
      uploading?: boolean;
      error?: string;
    }>
  >([]);
  const [darRemarks, setDarRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Editable fields for Higher Studies
  const [editableFields, setEditableFields] = useState({
    courseName: "",
    partTimeOrCorresp: "",
    institute: "",
  });

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Upload file to AWS
  const uploadFile = async (
    file: File
  ): Promise<{ key: string; url: string } | null> => {
    try {
      const base64String = await fileToBase64(file);
      const result = await dispatch(
        uploadFileToAWS({ filestring: base64String })
      ).unwrap();
      return {
        key: result.data.key,
        url: result.data.url,
      };
    } catch (error) {
      console.error("File upload failed:", error);
      return null;
    }
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles: { file: File; key: string; url: string; uploading: boolean }[] = [];
    const errors: string[] = [];

    // Validate each file before processing
    fileArray.forEach((file) => {
      // Validate file type
      if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
        errors.push(
          `${file.name}: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(", ").toUpperCase()}`
        );
        return;
      }

      // Validate file size (2MB limit)
      if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
        errors.push(
          `${file.name}: File size (${formatFileSize(file.size)}) exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`
        );
        return;
      }

      validFiles.push({
        file,
        key: "",
        url: "",
        uploading: true,
      });
    });

    // Show validation errors
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
    }

    // If no valid files, return early
    if (validFiles.length === 0) {
      e.target.value = "";
      return;
    }

    // Add valid files to local state with uploading status
    setUploadedDocuments((prev) => [...prev, ...validFiles]);

    // Upload each valid file to AWS
    for (let i = 0; i < validFiles.length; i++) {
      const fileInfo = validFiles[i];
      try {
        const uploadResult = await uploadFile(fileInfo.file);

        if (uploadResult) {
          setUploadedDocuments((prev) =>
            prev.map((f) =>
              f.file === fileInfo.file
                ? {
                  ...f,
                  key: uploadResult.key,
                  url: uploadResult.url,
                  uploading: false,
                }
                : f
            )
          );
          toast.success(`"${fileInfo.file.name}" uploaded successfully`);
        } else {
          setUploadedDocuments((prev) =>
            prev.map((f) =>
              f.file === fileInfo.file
                ? { ...f, error: "Upload failed", uploading: false }
                : f
            )
          );
          toast.error(`Failed to upload "${fileInfo.file.name}"`);
        }
      } catch (error) {
        console.error("File upload error:", error);
        setUploadedDocuments((prev) =>
          prev.map((f) =>
            f.file === fileInfo.file
              ? { ...f, error: "Upload failed", uploading: false }
              : f
          )
        );
        toast.error(`Failed to upload "${fileInfo.file.name}"`);
      }
    }

    // Clear the input
    e.target.value = "";
  };

  // Remove file and delete from AWS
  const removeDocument = async (fileIndex: number) => {
    const fileToRemove = uploadedDocuments[fileIndex];

    if (fileToRemove.key) {
      try {
        await dispatch(deleteFileFromAWS({ key: fileToRemove.key })).unwrap();
        toast.success(`"${fileToRemove.file.name}" deleted successfully`);
      } catch (error) {
        console.error("Failed to delete file from AWS:", error);
        toast.error(`Failed to delete "${fileToRemove.file.name}" from server`);
      }
    }

    setUploadedDocuments((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  // Handle Approve/Reject
  const handleApproveReject = async (action: "approve" | "reject") => {
    if (action === "reject" && !remarks.trim()) {
      toast.error("Approver remarks/comments are mandatory for rejection.");
      return;
    }
    if (nocType === "NOC_HIGHERSTUDY" && !remarks.trim()) {
      toast.error("Please add remarks before " + action + "ing");
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare payload for API
      const payload: any = {
        Approve_Reject_flag: action === "approve" ? "A" : "R",
        ReferenceNumber: nocData.referenceNo,
      };

      if (nocType === "NOC_HIGHERSTUDY") {
        payload.remarks = "";
        payload.hRemarks = remarks.trim();
        payload.courseName = editableFields.courseName;
        payload.partTimeOrCorresp = editableFields.partTimeOrCorresp;
        payload.institute = editableFields.institute;

        // Add documents if uploaded
        if (uploadedDocuments.length > 0) {
          payload.documents = uploadedDocuments.map((doc) => ({
            fileName: doc.key,
            typeOfAttachedDoc: "Upload Documents",
          }));
        }
      } else {
        payload.remarks = remarks.trim();
        payload.remarks1 = darRemarks.trim();
        payload.DandARStatus = nocData.darStatusDetails.status;
        payload.dandARPenaltyEffective = nocData.darStatusDetails.penaltyEffectiveDate;
        payload.FinalDandARVigilance = nocData.finalDarVigilance.status;
        payload.PostAppliedFor = nocData.outsideJobDetails.postAppliedFor;
        payload.OrganisationInWhich = nocData.outsideJobDetails.organizationName;
      }

      const apiUrl = nocType === "NOC_OUTJOB"
        ? API_URLS.NOC.APPROVE_REJECT_OUTSIDE_JOB
        : API_URLS.NOC.APPROVE_REJECT_HIGHER_EDUCATION;

      // Call the approve/reject API
      const response = await Api.post(apiUrl, payload);

      // Check for success
      if (
        response?.data?.status === 200 ||
        response?.data?.statusCode === 200 ||
        response?.statusCode === 200
      ) {
        const successMessage = response?.data?.message ||
          (action === "approve"
            ? "NOC request approved successfully!"
            : "NOC request rejected successfully!");
        toast.success(successMessage);

        setTimeout(() => {
          window.history.back();
        }, 1500);
      } else {
        // Handle error response
        const errorMessage = response?.data?.message ||
          `Failed to ${action} NOC request`;
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error(`Failed to ${action} NOC:`, error);

      // Extract error message
      const errorMessage = error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        `Failed to ${action} NOC request. Please try again.`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sample NOC data - in real app this would come from API based on ID
  const nocData = {
    id: parseInt(id || '20990'),
    title: "Application for NOC for Outside Job for Employee-00020990",
    referenceNo: "00020990000008",
    status: "New",
    employee: {
      name: "RAGHUVEER PRASAD",
      designation: "Sys.Analyst",
      id: "15136",
      timeStamp: "19.06.2025 16:17:33"
    },
    personalDetails: {
      name: "OSHO ADITYA SAXENA",
      dateOfJoining: "24.01.2016",
      designation: "Manager",
      dateOfConfirmation: "24.01.2020",
      contactNumber: "9888888888",
      dateOfRetirement: "28.02.2040",
      uap: "59 A",
      designation2: "MANAGER",
      employmentStatus: "Direct recruit",
      department: "IT",
      emailId: "1119@gmail.com",
      dateOfBirth: "28.02.1980",
      payScale: "50000-160000",
      age: "46",
      category: "General"
    },
    nocDetails: {
      probationPeriod: "Yes",
      empPanelExamination: "Yes",
      regularServiceComplaint: "Yes",
      disciplinaryProceeding: "Yes",
      placeOfTraining: "UA",
      yearOfTraining: "Nil",
      applications: "NA",
      placeOfPosting: "Nil",
      organization: "Nil",
      firstReportOfficer: "Test",
      reportingOfficer: "Test",
      acknowledgementNo: "123",
      acknowledgementDate: "19.06.2025",
      organizationRequested: "Nil",
      receiptDate: "19.06.2025"
    },
    outsideJobDetails: {
      whetherProbationConfirmed: "Yes",
      whetherOneYearServiceAfterProbation: "No",
      trainingAbroadByDMRC: "Yes",
      placeOfTrainingAbroad: "USA",
      yearOfTraining: "2023",
      applicationsForwardedInYear: "1st",
      placeOfPosting: "Delhi",
      natureOfOutsideJob: "Deputation",
      postAppliedFor: "SE",
      newspaperWebsite: "Times of India",
      organizationName: "Tech Solutions",
      advertisementNo: "ADV-2024",
      advertisementDate: "01-08-2024",
      lastDateOfReceipt: "31-08-2024"
    },
    otherDetails: {
      darPending: "No",
      vigilancePending: "No"
    },
    approverHistory: [
      {
        name: "OSHO Aditya Saxena",
        id: "00020990",
        timeStamp: "19.06.2025 16:17:33",
        status: "New"
      },
      {
        name: "Kshitija Prashap Singh",
        id: "00020101",
        timeStamp: "19.06.2025 16:17:47",
        status: "Approved"
      },
      {
        name: "Binoy Kumar Gupta",
        id: "00020103",
        timeStamp: "19.06.2025 16:17:47",
        status: "Approved"
      },
      {
        name: "Birender Palsangi",
        id: "00020103",
        timeStamp: "19.06.2025 16:17:47",
        status: "Approved"
      },
      {
        name: "Rajat Bhatnaa",
        id: "00020103",
        timeStamp: "19.06.2025 16:17:47",
        status: "Approved"
      },
      {
        name: "A VENKATA KRISHNA",
        id: "00000000",
        timeStamp: "19.06.2025 16:17:47",
        status: "Approved"
      },
      {
        name: "Rajendra Sradhey",
        id: "00020103",
        timeStamp: "19.06.2025 16:17:47",
        status: "Approved"
      },
      {
        name: "Amit Kumar",
        id: "00020103",
        timeStamp: "10.06.2025 16:17:47",
        status: "Approved"
      }
    ],
    educationDetails: [
      {
        courseName: "BA",
        courseLevel: "BA",
        dateOfAdmission: "6.10.2016",
        dateOfAdmission2: "6.06.2006",
        dateOfPassing: "3.06.2007",
        dateOfPassing2: "3.06.2019",
        university: "Kurukshetra University",
        university2: "Kurukshetra University",
        isCourseFunding: "Yes",
        isCourseFunding2: "Yes"
      }
    ],
    educationalDetailsOutJob: [
      {
        courseName: "B.Tech",
        dateOfAdmission: "01.08.2010",
        dateOfPassing: "30.06.2014",
        whenQualificationAcquired: "Before Joining",
        affiliatedByUGCAICTE: "Yes"
      }
    ],
    attachments: [
      {
        name: "NOC bank statement",
        type: "Type of Document",
        uploaded: true
      },
      { name: "Cert.pdf", type: "Certificate" },
      { name: "Application.pdf", type: "Application" }
    ],
    additionalInfo: {
      priorityImportant90Years: "No",
      priorityImportantVigilance: "No",
      priorityImportantVigilanceCase: "Nil",
      remarks: "Test Remarks"
    },
    darStatusDetails: {
      status: "No D&AR contemplated against the employee",
      penaltyEffectiveDate: "-",
      remarks: "Nil"
    },
    finalDarVigilance: {
      status: "Clear from vigilance angle"
    },
  };

  if (isMobile) {
    return (
      <div className="page-wrapper mobile-leave-approval">
        <div className="content">
          {/* Mobile Header */}
          <div className="mobile-header bg-danger text-white p-3 mb-3">
            <div className="d-flex align-items-center">
              <Link to={routes.todoWorklist.tasks} className="text-white me-3">
                <i className="ti ti-arrow-left fs-5"></i>
              </Link>
              <div>
                <h6 className="mb-0 text-white">To Do Work List</h6>
                <small className="text-white-50">NOC Application</small>
              </div>
            </div>
          </div>

          {/* Title Card */}
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-2">{nocData.title}</h6>
              <div className="small text-muted">
                <div>Reference No.: {nocData.referenceNo}</div>
                <div>Status: {nocData.status}</div>
              </div>
            </div>
          </div>

          {/* Overview Card */}
          <div className="card mb-3">
            <div className="card-header bg-light">
              <h6 className="mb-0">Overview</h6>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <h6 className="text-primary mb-2">EMPLOYEE</h6>
                <div className="small">
                  <div>{nocData.employee.name} ({nocData.employee.designation})</div>
                  <div>ID: {nocData.employee.id}</div>
                  <div>Time Stamp: {nocData.employee.timeStamp}</div>
                </div>
              </div>

              <div className="mb-3">
                <h6 className="text-success mb-2">APPROVER HISTORY</h6>
                <div className="small">
                  {nocData.approverHistory.slice(0, 3).map((approver, index) => (
                    <div key={index} className="mb-2 p-2 bg-light rounded">
                      <div className="fw-medium">{approver.name}</div>
                      <div>ID: {approver.id}</div>
                      <div>Time Stamp: {approver.timeStamp}</div>
                      <span className={`badge ${approver.status === 'Approved' ? 'bg-success' : 'bg-primary'}`}>
                        {approver.status}
                      </span>
                    </div>
                  ))}
                  {nocData.approverHistory.length > 3 && (
                    <div className="text-muted small">
                      +{nocData.approverHistory.length - 3} more approvers...
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <h6 className="text-info mb-2">REFERENCE NO.</h6>
                <div className="fw-bold">{nocData.referenceNo}</div>
              </div>
            </div>
          </div>

          {/* Personal Details Card */}
          <div className="card mb-3">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0 text-white">Personal Details</h6>
            </div>
            <div className="card-body small">
              <div className="row g-2">
                <div className="col-6">
                  <div className="text-muted">Name:</div>
                  <div className="fw-medium">{nocData.personalDetails.name}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted">Date of Joining:</div>
                  <div className="fw-medium">{nocData.personalDetails.dateOfJoining}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted">Designation:</div>
                  <div className="fw-medium">{nocData.personalDetails.designation}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted">Contact:</div>
                  <div className="fw-medium">{nocData.personalDetails.contactNumber}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted">Department:</div>
                  <div className="fw-medium">{nocData.personalDetails.department}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted">Email:</div>
                  <div className="fw-medium">{nocData.personalDetails.emailId}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Education Details Card */}
          <div className="card mb-3">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0 text-white">Education Details</h6>
            </div>
            <div className="card-body small">
              {nocData.educationDetails.map((edu, index) => (
                <div key={index} className="mb-2">
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="text-muted">Course Name:</div>
                      <div className="fw-medium">{edu.courseName}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Course Level:</div>
                      <div className="fw-medium">{edu.courseLevel}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Date of Admission:</div>
                      <div className="fw-medium">{edu.dateOfAdmission}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Date of Passing:</div>
                      <div className="fw-medium">{edu.dateOfPassing}</div>
                    </div>
                    <div className="col-12">
                      <div className="text-muted">University:</div>
                      <div className="fw-medium">{edu.university}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Attachments Card */}
          <div className="card mb-3">
            <div className="card-header bg-warning text-dark">
              <h6 className="mb-0">Attachments</h6>
            </div>
            <div className="card-body">
              {nocData.attachments.map((attachment, index) => (
                <div key={index} className="d-flex align-items-center mb-2">
                  <i className="ti ti-file-text text-primary me-2"></i>
                  <div className="flex-grow-1">
                    <div className="small fw-medium">{attachment.name}</div>
                    <div className="small text-muted">{attachment.type}</div>
                  </div>
                  <span className="badge bg-success">Uploaded</span>
                </div>
              ))}
            </div>
          </div>

          {/* Fixed Bottom Action Buttons */}
          <div className="fixed-bottom bg-white border-top p-3">
            <div className="row g-2">
              <div className="col-6">
                <button className="btn btn-success w-100">
                  <i className="ti ti-check me-1"></i>
                  Approve
                </button>
              </div>
              <div className="col-6">
                <button className="btn btn-danger w-100">
                  <i className="ti ti-x me-1"></i>
                  Reject
                </button>
              </div>
            </div>
          </div>

          {/* Bottom padding */}
          <div style={{ height: '80px' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center mb-2">
              <Link
                to={routes.todoWorklist.tasks}
                className="btn btn-outline-secondary btn-sm me-3"
              >
                <i className="ti ti-arrow-left me-1"></i>
                Back
              </Link>
              <div>
                <h3 className="page-title mb-1">NOC Application Details</h3>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.todoWorklist.tasks}>To Do Work List</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      NOC Details
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <span className="badge bg-primary me-2">ID: {nocData.id}</span>
            <span className={formatStatusBadgeClass(nocData.status)}>{formatStatusText(nocData.status)}</span>
          </div>
        </div>

        <div className="row">
          {/* Main Content */}
          <div className="col-lg-8">
            {/* Application Header */}
            <div className="card mb-4">
              <div className="card-header bg-danger text-white">
                <h5 className="card-title mb-0 text-white">
                  <i className="ti ti-file-certificate me-2"></i>
                  {nocData.title}
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="info-item mb-3">
                      <label className="text-muted small">Reference Number</label>
                      <div className="fw-bold fs-6">{nocData.referenceNo}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="info-item mb-3">
                      <label className="text-muted small">Application Status</label>
                      <div>
                        <span className={formatStatusBadgeClass(nocData.status)}>{formatStatusText(nocData.status)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Employee Information */}
            {nocType !== "NOC_OUTJOB" && (
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-user me-2"></i>
                    Employee Information
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Employee Name</label>
                        <div className="fw-bold">{nocData.employee.name}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Employee ID</label>
                        <div className="fw-bold">{nocData.employee.id ? parseInt(nocData.employee.id, 10) : nocData.employee.id}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Designation</label>
                        <div className="fw-bold">{nocData.employee.designation}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Application Submitted</label>
                        <div className="fw-bold">{nocData.employee.timeStamp}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Personal Details */}
            {nocType === "NOC_OUTJOB" ? (
              <div className="card mb-4">
                <div className="card-header bg-success text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-id me-2"></i>
                    Employee Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Name (Full, not in abbreviation)</label>
                        <div className="fw-bold">{nocData.personalDetails.name}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Employee ID</label>
                        <div className="fw-bold">{nocData.employee.id}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Designation</label>
                        <div className="fw-bold">{nocData.personalDetails.designation}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Date of Joining</label>
                        <div className="fw-bold">{nocData.personalDetails.dateOfJoining}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Employment status</label>
                        <div className="fw-bold">{nocData.personalDetails.employmentStatus}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Pay Scale</label>
                        <div className="fw-bold">{nocData.personalDetails.payScale || '-'}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Contact No</label>
                        <div className="fw-bold">{nocData.personalDetails.contactNumber}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Email Id</label>
                        <div className="fw-bold">{nocData.personalDetails.emailId}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Department</label>
                        <div className="fw-bold">{nocData.personalDetails.department}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Age</label>
                        <div className="fw-bold">{nocData.personalDetails.age || '-'}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Category</label>
                        <div className="fw-bold">{nocData.personalDetails.category || '-'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card mb-4">
                <div className="card-header bg-success text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-id me-2"></i>
                    Personal Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Name</label>
                        <div className="fw-bold">{nocData.personalDetails.name}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Date of Joining in DMRC</label>
                        <div className="fw-bold">{nocData.personalDetails.dateOfJoining}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Designation</label>
                        <div className="fw-bold">{nocData.personalDetails.designation}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Date of Confirmation/Absorption</label>
                        <div className="fw-bold">{nocData.personalDetails.dateOfConfirmation}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Contact Number</label>
                        <div className="fw-bold">{nocData.personalDetails.contactNumber}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Date of Retirement</label>
                        <div className="fw-bold">{nocData.personalDetails.dateOfRetirement}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">UAP</label>
                        <div className="fw-bold">{nocData.personalDetails.uap}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Designation</label>
                        <div className="fw-bold">{nocData.personalDetails.designation2}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Employment status</label>
                        <div className="fw-bold">{nocData.personalDetails.employmentStatus}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Department</label>
                        <div className="fw-bold">{nocData.personalDetails.department}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Email-Id</label>
                        <div className="fw-bold">{nocData.personalDetails.emailId}</div>
                      </div>
                      {nocType !== "NOC_HIGHERSTUDY" && nocType !== "NOC_EXINDIA" && (
                        <>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Date of Birth</label>
                            <div className="fw-bold">{nocData.personalDetails.dateOfBirth}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Age</label>
                            <div className="fw-bold">{nocData.personalDetails.age || "N/A"}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* NOC Specific Details */}
            {nocType === "NOC_OUTJOB" && (
              <div className="card mb-4">
                <div className="card-header bg-info text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-clipboard-check me-2"></i>
                    NOC Form Information
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Whether probation is confirmed by DMRC</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.whetherProbationConfirmed}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Whether 1 Year service after probation confirmed?</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.whetherOneYearServiceAfterProbation}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Has the employee been imparted training abroad by DMRC</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.trainingAbroadByDMRC}</div>
                      </div>
                      {(nocData.outsideJobDetails.trainingAbroadByDMRC === "Yes" || nocData.outsideJobDetails.trainingAbroadByDMRC === "Y") && (
                        <>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Place of Training Abroad</label>
                            <div className="fw-bold">{nocData.outsideJobDetails.placeOfTrainingAbroad}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Year of Training</label>
                            <div className="fw-bold">{nocData.outsideJobDetails.yearOfTraining}</div>
                          </div>
                        </>
                      )}
                      <div className="info-item mb-3">
                        <label className="text-muted small">No. of applications forwarded in the year</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.applicationsForwardedInYear}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Place of Posting</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.placeOfPosting}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Nature of Outside Job</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.natureOfOutsideJob}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Name of post applied for</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.postAppliedFor}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Newspaper/Website</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.newspaperWebsite}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Name of the organization in which applied</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.organizationName}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Advertisement No.</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.advertisementNo}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Advertisement Date</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.advertisementDate}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Last date of receipt of application</label>
                        <div className="fw-bold">{nocData.outsideJobDetails.lastDateOfReceipt}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {nocType !== "NOC_OUTJOB" && (
              <div className="card mb-4">
                <div className="card-header bg-info text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-clipboard-check me-2"></i>
                    NOC Application Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Whether probation is confirmed by DMRC</label>
                        <div className="fw-bold">
                          <span className={`badge ${nocData.nocDetails.probationPeriod === 'Yes' ? 'bg-success' : 'bg-danger'}`}>
                            {nocData.nocDetails.probationPeriod}
                          </span>
                        </div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">For employees who have cleared and gone through DMRC examination</label>
                        <div className="fw-bold">
                          <span className={`badge ${nocData.nocDetails.empPanelExamination === 'Yes' ? 'bg-success' : 'bg-danger'}`}>
                            {nocData.nocDetails.empPanelExamination}
                          </span>
                        </div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Has the employee been implicated in any disciplinary proceeding</label>
                        <div className="fw-bold">
                          <span className={`badge ${nocData.nocDetails.disciplinaryProceeding === 'Yes' ? 'bg-success' : 'bg-danger'}`}>
                            {nocData.nocDetails.disciplinaryProceeding}
                          </span>
                        </div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Place of Training Abroad</label>
                        <div className="fw-bold">{nocData.nocDetails.placeOfTraining}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Year of Training</label>
                        <div className="fw-bold">{nocData.nocDetails.yearOfTraining}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">No. of applications he worked in the year</label>
                        <div className="fw-bold">{nocData.nocDetails.applications}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Place of Posting</label>
                        <div className="fw-bold">{nocData.nocDetails.placeOfPosting}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Name of the organization is which applied</label>
                        <div className="fw-bold">{nocData.nocDetails.organization}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Name of First reporting officer</label>
                        <div className="fw-bold">{nocData.nocDetails.firstReportOfficer}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Reporting/ validating</label>
                        <div className="fw-bold">{nocData.nocDetails.reportingOfficer}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Acknowledgement No.</label>
                        <div className="fw-bold">{nocData.nocDetails.acknowledgementNo}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Acknowledgement Date</label>
                        <div className="fw-bold">{nocData.nocDetails.acknowledgementDate}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Educational Details */}
            {nocType === "NOC_OUTJOB" ? (
              <div className="card mb-4">
                <div className="card-header bg-warning text-dark">
                  <h5 className="card-title mb-0">
                    <i className="ti ti-school me-2"></i>
                    Educational Details
                  </h5>
                </div>
                <div className="card-body">
                  {nocData.educationalDetailsOutJob?.map((edu: any, index: number) => (
                    <div key={index} className="mb-4 border-bottom pb-3">
                      <h6 className="fw-bold text-primary mb-3">Education Details {index + 1}</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="info-item mb-3">
                            <label className="text-muted small">Course Name</label>
                            <div className="fw-bold">{edu.courseName}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Date of Admission</label>
                            <div className="fw-bold">{edu.dateOfAdmission}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Date of Passing</label>
                            <div className="fw-bold">{edu.dateOfPassing}</div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="info-item mb-3">
                            <label className="text-muted small">When Qualification Acquired</label>
                            <div className="fw-bold">{edu.whenQualificationAcquired}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Course affiliated by UGC/AICTE</label>
                            <div className="fw-bold">{edu.affiliatedByUGCAICTE}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="card mb-4">
                <div className="card-header bg-warning text-dark">
                  <h5 className="card-title mb-0">
                    <i className="ti ti-school me-2"></i>
                    Educational Details
                  </h5>
                </div>
                <div className="card-body">
                  {nocData.educationDetails.map((edu, index) => (
                    <div key={index} className="mb-4">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="info-item mb-3">
                            <label className="text-muted small">Course Name: BA</label>
                            <div className="fw-bold">{edu.courseName}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Course Level: BA</label>
                            <div className="fw-bold">{edu.courseLevel}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Date of Admission: 6.10.2016</label>
                            <div className="fw-bold">{edu.dateOfAdmission}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Date of Admission: 6.06.2006</label>
                            <div className="fw-bold">{edu.dateOfAdmission2}</div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="info-item mb-3">
                            <label className="text-muted small">Date of Passing: 3.06.2007</label>
                            <div className="fw-bold">{edu.dateOfPassing}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">Date of Passing: 3.06.2019</label>
                            <div className="fw-bold">{edu.dateOfPassing2}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">University: Kurukshetra University</label>
                            <div className="fw-bold">{edu.university}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">University: Kurukshetra University</label>
                            <div className="fw-bold">{edu.university2}</div>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="info-item mb-3">
                            <label className="text-muted small">Is course funded by DMRC</label>
                            <div className="fw-bold">
                              <span className={`badge ${edu.isCourseFunding === 'Yes' ? 'bg-success' : 'bg-danger'}`}>
                                {edu.isCourseFunding}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="info-item mb-3">
                            <label className="text-muted small">Is course funded by DMRC</label>
                            <div className="fw-bold">
                              <span className={`badge ${edu.isCourseFunding2 === 'Yes' ? 'bg-success' : 'bg-danger'}`}>
                                {edu.isCourseFunding2}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            <div className="card mb-4">
              <div className="card-header bg-secondary text-white">
                <h5 className="card-title mb-0 text-white">
                  <i className="ti ti-paperclip me-2"></i>
                  Attachments
                </h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Uploaded documents</th>
                        <th>Type of Document</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nocData.attachments.map((attachment, index) => (
                        <tr key={index}>
                          <td>{attachment.name}</td>
                          <td>{attachment.type}</td>
                          <td>
                            <span className="badge bg-success">Uploaded</span>
                          </td>
                          <td>
                            <Link to="#" className="btn btn-outline-primary btn-sm">
                              <i className="ti ti-download"></i>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            {nocType !== "NOC_OUTJOB" && (
              <div className="card mb-4">
                <div className="card-header bg-dark text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-info-circle me-2"></i>
                    Additional Information
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Whether any penalty imposed during last 10 years of DMRC Career</label>
                        <div className="fw-bold">
                          <span className={`badge ${nocData.additionalInfo.priorityImportant90Years === 'Yes' ? 'bg-success' : 'bg-danger'}`}>
                            {nocData.additionalInfo.priorityImportant90Years}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item mb-3">
                        <label className="text-muted small">Whether any pending important during last 10 years in any Vigilance case</label>
                        <div className="fw-bold">
                          <span className={`badge ${nocData.additionalInfo.priorityImportantVigilance === 'Yes' ? 'bg-success' : 'bg-danger'}`}>
                            {nocData.additionalInfo.priorityImportantVigilance}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="info-item mb-3">
                    <label className="text-muted small">Whether any penalty imposed during last 10 years in any Vigilance case</label>
                    <div className="bg-light p-3 rounded mt-1">
                      <p className="mb-0">{nocData.additionalInfo.priorityImportantVigilanceCase || 'No additional information provided'}</p>
                    </div>
                  </div>

                  <div className="info-item">
                    <label className="text-muted small">Remarks</label>
                    <div className="bg-light p-3 rounded mt-1">
                      <p className="mb-0">{nocData.additionalInfo.remarks || 'No remarks provided'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other (Vigilance) */}
            {nocType === "NOC_OUTJOB" && (
              <div className="card mb-4">
                <div className="card-header bg-dark text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-alert-triangle me-2"></i>
                    Other
                  </h5>
                </div>
                <div className="card-body">
                  <div className="info-item mb-3">
                    <label className="text-muted small">Whether any penalty imposed / DAR case pending</label>
                    <div className="fw-bold">
                      <span className={`badge ${nocData.otherDetails.darPending === 'Yes' ? 'bg-danger' : 'bg-success'}`}>
                        {nocData.otherDetails.darPending}
                      </span>
                    </div>
                  </div>
                  <div className="info-item">
                    <label className="text-muted small">Whether any penalty imposed / Vigilance case pending</label>
                    <div className="fw-bold">
                      <span className={`badge ${nocData.otherDetails.vigilancePending === 'Yes' ? 'bg-danger' : 'bg-success'}`}>
                        {nocData.otherDetails.vigilancePending}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DAR Status of the employees (HR) */}
            {nocType === "NOC_OUTJOB" && (
              <div className="card mb-4 border-danger">
                <div className="card-header bg-danger text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-alert-circle me-2"></i>
                    DAR Status of the employees
                  </h5>
                </div>
                <div className="card-body">
                  <div className="info-item mb-3">
                    <label className="text-muted small">DAR Status</label>
                    <div className="fw-bold">{nocData.darStatusDetails.status}</div>
                  </div>
                  <div className="info-item mb-3">
                    <label className="text-muted small">D&AR Penalty Effective</label>
                    <div className="fw-bold">{nocData.darStatusDetails.penaltyEffectiveDate}</div>
                  </div>
                  <div className="info-item">
                    <label className="text-muted small">Remarks by Controlling</label>
                    <div className="bg-light p-3 rounded mt-1">
                      <p className="mb-0">{nocData.darStatusDetails.remarks}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Final DAR Vigilance status HR */}
            {nocType === "NOC_OUTJOB" && (
              <div className="card mb-4 border-info">
                <div className="card-header bg-info text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="ti ti-eye me-2"></i>
                    Final DAR Vigilance status HR
                  </h5>
                </div>
                <div className="card-body">
                  <div className="info-item mb-3">
                    <label className="text-muted small">Final DAR Vigilance status HR</label>
                    <div className="bg-light p-3 rounded mt-1">
                      <p className="mb-0">{nocData.finalDarVigilance.status}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Declaration */}
            {nocType === "NOC_OUTJOB" && (
              <div className="card mb-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0">
                    <i className="ti ti-certificate me-2 text-primary"></i>
                    Declaration
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="mb-0 text-muted small">
                    <li className="mb-2">I hereby declare that the information given above is true.</li>
                    <li>I do declare that I will strictly abide by the rules and regulations. If any information given above is found incorrect, my application is liable to be rejected.</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Approver Action Block */}
            {(nocType === "NOC_HIGHERSTUDY" || nocType === "NOC_OUTJOB") && (
              <>
                {nocType === "NOC_HIGHERSTUDY" && (
                  <div className="card mb-4 border-warning">
                    <div className="card-header bg-warning text-dark">
                      <h5 className="card-title mb-0">
                        <i className="ti ti-edit me-2"></i>
                        Editable Details (For HR)
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Course Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editableFields.courseName}
                            onChange={(e) => setEditableFields({ ...editableFields, courseName: e.target.value })}
                            placeholder="Enter course name"
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Institute</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editableFields.institute}
                            onChange={(e) => setEditableFields({ ...editableFields, institute: e.target.value })}
                            placeholder="Enter institute name"
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Mode of Course</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editableFields.partTimeOrCorresp}
                            onChange={(e) => setEditableFields({ ...editableFields, partTimeOrCorresp: e.target.value })}
                            placeholder="e.g., Part-Time / Correspondence"
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* HR Remarks Section */}
                <div className="card mb-4 border-primary">
                  <div className="card-header bg-primary text-white">
                    <h5 className="card-title mb-0 text-white">
                      <i className="ti ti-message-circle me-2"></i>
                      HR Remarks
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="form-group mb-3">
                      <label className="form-label fw-bold">
                        Add Your Remarks <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={5}
                        placeholder="Enter your remarks here..."
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        disabled={isSubmitting}
                      />
                      <small className="text-muted">
                        Required before approving or rejecting the request
                      </small>
                    </div>
                    {nocType === "NOC_OUTJOB" && (
                      <div className="form-group">
                        <label className="form-label fw-bold">
                          D&AR / Rejection Remarks
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="Enter D&AR or rejection remarks here..."
                          value={darRemarks}
                          onChange={(e) => setDarRemarks(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload Additional Documents Section */}
                <div className="card mb-4 border-info">
                  <div className="card-header bg-info text-white">
                    <h5 className="card-title mb-0 text-white">
                      <i className="ti ti-upload me-2"></i>
                      Upload Additional Documents (For HR Use Only)
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-info mb-3">
                      <i className="ti ti-info-circle me-2"></i>
                      Upload any additional documents required for processing this NOC request.
                      Documents are automatically uploaded to AWS S3.
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">Select Documents</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={handleFileUpload}
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        disabled={isSubmitting}
                      />
                      <small className="text-muted d-block mt-1">
                        Files will be uploaded automatically to AWS upon selection
                      </small>
                    </div>

                    {/* Uploaded Documents Table */}
                    {uploadedDocuments.length > 0 && (
                      <div className="table-responsive mt-4">
                        <table className="table table-bordered table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Document Name</th>
                              <th>File Type</th>
                              <th>Size (KB)</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadedDocuments.map((doc, index) => (
                              <tr key={index}>
                                <td>
                                  {doc.uploading ? (
                                    <span className="text-muted">
                                      <i className="ti ti-loader animate-spin me-2"></i>
                                      {doc.file.name}
                                    </span>
                                  ) : doc.error ? (
                                    <span className="text-danger">
                                      <i className="ti ti-alert-circle me-2"></i>
                                      {doc.file.name}
                                    </span>
                                  ) : (
                                    <span className="text-success">
                                      <i className="ti ti-check me-2"></i>
                                      {doc.file.name}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <span className="badge bg-secondary">
                                    {doc.file.name.split(".").pop()?.toUpperCase()}
                                  </span>
                                </td>
                                <td>{(doc.file.size / 1024).toFixed(2)}</td>
                                <td>
                                  {doc.uploading ? (
                                    <span className="badge bg-warning">
                                      <i className="ti ti-loader animate-spin me-1"></i>
                                      Uploading...
                                    </span>
                                  ) : doc.error ? (
                                    <span className="badge bg-danger">
                                      <i className="ti ti-x me-1"></i>
                                      Failed
                                    </span>
                                  ) : (
                                    <span className="badge bg-success">
                                      <i className="ti ti-check me-1"></i>
                                      Uploaded
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeDocument(index)}
                                    disabled={doc.uploading || isSubmitting}
                                  >
                                    {doc.uploading ? (
                                      <>
                                        <i className="ti ti-loader animate-spin me-1"></i>
                                        Wait...
                                      </>
                                    ) : (
                                      <>
                                        <i className="ti ti-trash me-1"></i>
                                        Delete
                                      </>
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {uploadedDocuments.length === 0 && (
                      <div className="text-center py-4 bg-light rounded">
                        <i className="ti ti-file-upload fs-1 text-muted"></i>
                        <p className="text-muted mt-2 mb-0">No documents uploaded yet</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons for Higher Studies */}
                <div className="card border-warning">
                  <div className="card-header bg-warning text-dark">
                    <h5 className="card-title mb-0">
                      <i className="ti ti-settings me-2"></i>
                      Take Action
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-warning mb-3">
                      <i className="ti ti-alert-triangle me-2"></i>
                      <strong>Important:</strong> Please ensure you have added remarks before approving or rejecting this request.
                    </div>

                    <div className="d-flex gap-3 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-success btn-lg px-5"
                        onClick={() => handleApproveReject("approve")}
                        disabled={isSubmitting || !remarks.trim()}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-check me-2"></i>
                            Approve Request
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-lg px-5"
                        onClick={() => handleApproveReject("reject")}
                        disabled={isSubmitting || !remarks.trim()}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-x me-2"></i>
                            Reject Request
                          </>
                        )}
                      </button>
                    </div>

                    {!remarks.trim() && (
                      <div className="text-center mt-3">
                        <small className="text-danger">
                          <i className="ti ti-alert-circle me-1"></i>
                          Please add remarks to enable action buttons
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Approval History Sidebar */}
          <div className="col-lg-4">
            <div className="card sticky-top" style={{ top: '20px' }}>
              <div className="card-header bg-success text-white">
                <h5 className="card-title mb-0 text-white">
                  <i className="ti ti-users me-2"></i>
                  Approver History
                </h5>
              </div>
              <div className="card-body">
                <div className="timeline">
                  {nocData.approverHistory.map((approver, index) => (
                    <div key={index} className="timeline-item mb-3">
                      <div className="d-flex">
                        <div className="me-3">
                          <div className={`rounded-circle d-flex align-items-center justify-content-center ${approver.status === 'Approved' ? 'bg-success' : 'bg-primary'
                            }`} style={{ width: '40px', height: '40px' }}>
                            <i className={`ti ${approver.status === 'Approved' ? 'ti-check' : 'ti-clock'} text-white`}></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{approver.name}</h6>
                          <p className="text-muted small mb-1">ID: {approver.id}</p>
                          <p className="text-muted small mb-1">{approver.timeStamp}</p>
                          <span className={`badge ${approver.status === 'Approved' ? 'bg-success' : 'bg-primary'}`}>
                            {approver.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="d-grid gap-2 mt-4 pt-4 border-top">
                  <Link
                    to={routes.todoWorklist.tasks}
                    className="btn btn-outline-secondary"
                  >
                    <i className="ti ti-arrow-left me-2"></i>
                    Back to Worklist
                  </Link>
                  <div className="text-center">
                    <small className="text-muted">
                      Use approve/reject buttons in the worklist to take action
                    </small>
                  </div>
                </div>

                {/* Quick Summary */}
                <div className="mt-4 pt-4 border-top">
                  <h6 className="fw-bold mb-3">Quick Summary</h6>
                  <div className="small">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Employee:</span>
                      <span className="fw-medium">{nocData.employee.name}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Application Type:</span>
                      <span className="fw-medium">Outside Job NOC</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Reference No:</span>
                      <span className="fw-medium">{nocData.referenceNo}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Status:</span>
                      <span className={formatStatusBadgeClass(nocData.status)}>{formatStatusText(nocData.status)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Spinner Animation CSS */}
      <style>
        {`
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default NOCApprovalDetailPage;
